import { ActivationForm } from '@/features/authentication/Activation/fragments/NewPasswordFragment';
import { useLayoutEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import {
  authActions,
  authActions as actions,
  AuthState,
  Fetching,
} from './slice';
import { ValidationErrorType } from '@/types/ValidationError';

import { useInviteCode } from '@/features/authentication/Activation/useInviteCode.tsx';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from '@/routes/appRoutes.ts';

export const useAuthState = () =>
  useSelector<RootState, AuthState>((state) => state.auth);

export const useAccountActivation = ({
  inviteCode,
}: {
  inviteCode: string | undefined;
}) => {
  const state = useAuthState();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const error = useMemo(() => {
    return Object.keys(state.serverValidation.errors)[0] as ValidationErrorType;
  }, [state.serverValidation.errors]);
  useLayoutEffect(() => {
    if (inviteCode) {
      dispatch(actions.requestInviteExists({ inviteCode }));
    } else {
      navigate(AppRoutes.AUTH);
    }
  }, [dispatch, inviteCode, navigate]);

  const handlePrivacyPolicyNext = () => {
    if (state.userToInvite?.phone) {
      navigate(AppRoutes.PROFILE);
    } else {
      dispatch(authActions.goToStep('pin-code'));
    }
  };

  return {
    fetching: state.fetching?.[Fetching.CheckInvite],
    error,
    handlePrivacyPolicyNext,
  };
};

export const useActivationNewPasswordForm = () => {
  const state = useAuthState();
  const dispatch = useDispatch();
  const inviteCode = useInviteCode();
  const callbacks = useMemo(() => {
    return {
      handleSubmit: ({ password, phone }: ActivationForm) => {
        if (inviteCode && password && phone) {
          dispatch(
            actions.requestActivationSetNewPassword({
              password,
              phone,
              inviteCode,
            })
          );
        } else {
          console.error(
            `One of parameters is not provided\nInvite code: ${
              inviteCode ?? ''
            }\nPassword: ${password ?? ''}\nPhone number: ${phone ?? ''}`
          );
        }
      },
      resetErrors: () => {
        dispatch(actions.resetErrors());
      },
    };
  }, [dispatch, inviteCode]);

  return {
    errors: state.serverValidation.fieldErrors,
    ...callbacks,
  };
};
